// Load polyfills first, before anything else
import "./config/polyfills";

import * as Sentry from "@sentry/react";
import * as ReactDOMClient from "react-dom/client";
import App from "./App.jsx";

// CSS imports optimized for loading performance
import "./index.css";
import "./styles/icon-styles.css";

// Initialize Sentry EARLY - before app renders to catch all errors
const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Check if DSN is configured
  if (!dsn || dsn === "your-sentry-dsn") {
    console.warn(
      "[Sentry] VITE_SENTRY_DSN not configured. Error tracking is disabled.\n" +
        "Add VITE_SENTRY_DSN to your .env file to enable error tracking."
    );
    return false;
  }

  // Initialize Sentry with valid DSN (works in both dev and prod)
  const environment = import.meta.env.DEV
    ? "development"
    : import.meta.env.VITE_NODE_ENV || "production";

  Sentry.init({
    dsn,
    environment,
    // Release version for tracking deployments
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    // Performance monitoring - higher sample rate in dev
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.3,
    // Session replay for errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Enable in both dev and prod
    enabled: true,
    // Debug mode disabled to reduce console noise (set to true for Sentry troubleshooting)
    debug: false,
    // Add tags for filtering
    initialScope: {
      tags: {
        environment,
        app: "artifact-chat",
      },
    },
    // Ignore common non-actionable errors (be selective)
    ignoreErrors: [
      // Browser quirks - not actionable
      "ResizeObserver loop",
      // User-initiated cancellations
      "AbortError",
      // Extension interference
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
    ],
    // Filter out noisy breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Ignore console.log breadcrumbs in production
      if (breadcrumb.category === "console" && !import.meta.env.DEV) {
        return null;
      }
      return breadcrumb;
    },
  });

  // Expose Sentry globally for testing via console
  if (import.meta.env.DEV) {
    window.Sentry = Sentry;
  }

  console.info(`[Sentry] Initialized for ${environment} environment`);
  return true;
};

// Initialize Sentry immediately (before render)
const sentryEnabled = initSentry();

// Handle chunk loading failures from stale deployments
// When a new deployment occurs, old cached pages may reference chunks that no longer exist
// This auto-recovers by refreshing the page (once, to prevent infinite loops)
const isChunkLoadError = (message, target) => {
  if (!message && !target) return false;

  const msg = message?.toLowerCase() || "";

  // Check error message patterns
  const messagePatterns = [
    "failed to fetch dynamically imported module",
    "loading chunk",
    "loading css chunk",
    "error loading dynamically imported module",
    "failed to load module script",
    "net::err_aborted",
    "load failed", // Catches SW and script load failures on mobile Safari
  ];

  if (messagePatterns.some((pattern) => msg.includes(pattern))) {
    return true;
  }

  // Check if it's a script/link loading error (404 for assets)
  if (target?.tagName === "SCRIPT" || target?.tagName === "LINK") {
    const src = target.src || target.href || "";
    // Check if it's an asset file that failed to load
    if (
      src.includes("/assets/") &&
      (src.endsWith(".js") || src.endsWith(".css"))
    ) {
      return true;
    }
  }

  return false;
};

const handleChunkLoadError = (event) => {
  const message = event.message || event.reason?.message || "";
  const target = event.target;

  if (!isChunkLoadError(message, target)) {
    return;
  }

  // Prevent infinite reload loops - only retry once per session
  const reloadKey = "chunk-error-reload";
  const hasReloaded = sessionStorage.getItem(reloadKey);

  if (!hasReloaded) {
    sessionStorage.setItem(reloadKey, "true");
    console.info("[ChunkLoader] Stale deployment detected, refreshing page...");
    window.location.reload();
  } else {
    // Don't clear flag here - it will be cleared on successful load
    // This prevents infinite loops if reload also fails
    console.error(
      "[ChunkLoader] Chunk load failed after refresh. A new version may have been deployed - please refresh manually or clear your browser cache."
    );
  }
};

// Listen for script/link loading errors (use capture to catch errors that don't bubble)
window.addEventListener("error", handleChunkLoadError, true);

// Also handle unhandled promise rejections (dynamic imports throw these)
window.addEventListener("unhandledrejection", (event) => {
  handleChunkLoadError(event);
});

// OAuth redirect handling for localhost - DISABLED FOR DEVELOPMENT
// This redirect was causing development authentication to fail
// Only enable this in production builds or when explicitly needed
if (
  // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
  false &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
) {
  const urlParams = new URLSearchParams(window.location.search);
  const oauthCode = urlParams.get("code");

  if (oauthCode) {
    const productionUrl = `https://artifact.chat${window.location.pathname}${window.location.search}`;
    window.location.replace(productionUrl);
  }
}

const rootElement = document.getElementById("root");

// Persist root across HMR updates to prevent "createRoot on same container" warning
let root;
if (import.meta.hot) {
  root = import.meta.hot.data.root;
}
if (!root) {
  root = ReactDOMClient.createRoot(rootElement);
}
if (import.meta.hot) {
  import.meta.hot.data.root = root;
}

// Fallback UI for catastrophic errors
const ErrorFallback = ({ error }) => (
  <div style={{ padding: "20px", textAlign: "center" }}>
    <h1>Something went wrong</h1>
    <p>We&apos;ve been notified and are working on a fix.</p>
    <button onClick={() => window.location.reload()}>Reload Page</button>
    {import.meta.env.DEV && (
      <pre style={{ textAlign: "left", marginTop: "20px", fontSize: "12px" }}>
        {error?.message}
      </pre>
    )}
  </div>
);

// Render the app with Sentry Error Boundary
root.render(
  sentryEnabled ? (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <App />
    </Sentry.ErrorBoundary>
  ) : (
    <App />
  )
);

// Performance optimization: Mark app as ready
requestAnimationFrame(() => {
  document.documentElement.classList.remove("loading");
  if (rootElement) {
    rootElement.classList.add("js-loading-complete");
  }

  // Clear chunk error reload flag on successful load
  // This allows future chunk errors to trigger a refresh
  sessionStorage.removeItem("chunk-error-reload");

  // Mark performance metrics
  if (window.performanceMetrics) {
    window.performanceMetrics.markMetric("React App Rendered");
  }
});

// Hot module replacement
if (import.meta.hot) {
  import.meta.hot.accept();
}
